import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Grid,
    Typography,
    Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, useMap, Polyline, Popup as LeafletPopup, FeatureGroup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import L from "leaflet";
import { EditControl } from 'react-leaflet-draw';
import { useObtenerRutasTroncalQgis } from '@/shared/actions/erp/cobertura-qgis/cobertura-qgis.actions';

const ChangeMapView: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    React.useEffect(() => {
        if (map) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);
    return null;
};

const SearchControl = () => {
    const map = useMap();
    const markerRef = useRef<L.Marker | null>(null);

    useEffect(() => {
        const geocoder = (L.Control as any).Geocoder.nominatim();
        const control = (L.Control as any).geocoder({
            geocoder,
            defaultMarkGeocode: false,
        }).on("markgeocode", function (e: any) {
            const latlng = e.geocode.center;
            if (markerRef.current) {
                map.removeLayer(markerRef.current);
            }
            const newMarker = L.marker(latlng).addTo(map);
            markerRef.current = newMarker;
            map.setView(latlng, 10);
        });

        control.addTo(map);

        return () => {
            control.remove();
            if (markerRef.current) {
                map.removeLayer(markerRef.current);
            }
        };
    }, [map]);

    return null;
};

export default function TabRutasTroncal() {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [centerMap] = useState<[number, number]>([-0.3208730168341767, -78.47201823285198])

    const obtenerRutasTroncales = useObtenerRutasTroncalQgis();

    useEffect(() => {
        const LAny = L as any;

        if (!LAny.drawLocal) return;

        LAny.drawLocal.draw.toolbar.buttons.polyline = 'Dibujar línea';
        LAny.drawLocal.draw.toolbar.actions.text = 'Cancelar';
        LAny.drawLocal.draw.toolbar.finish.text = 'Finalizar';
        LAny.drawLocal.draw.toolbar.undo.text = 'Deshacer';

        LAny.drawLocal.draw.handlers.polyline.tooltip.start = 'Haz clic para comenzar';
        LAny.drawLocal.draw.handlers.polyline.tooltip.cont = 'Haz clic para continuar';
        LAny.drawLocal.draw.handlers.polyline.tooltip.end = 'Haz clic para terminar';

        // Botones principales
        LAny.drawLocal.edit.toolbar.buttons.edit = 'Editar elementos';
        LAny.drawLocal.edit.toolbar.buttons.remove = 'Eliminar elementos';
        LAny.drawLocal.edit.toolbar.actions.save.text = 'Guardar';
        LAny.drawLocal.edit.toolbar.actions.cancel.text = 'Cancelar';
        LAny.drawLocal.edit.toolbar.actions.clearAll.text = 'Eliminar todo';
        LAny.drawLocal.edit.toolbar.actions.clearAll.title = 'Eliminar todos los elementos';
        LAny.drawLocal.edit.handlers.edit.tooltip.text = 'Arrastra los puntos para editar';
        LAny.drawLocal.edit.handlers.edit.tooltip.subtext = 'Haz clic en cancelar para deshacer cambios';
        LAny.drawLocal.edit.handlers.remove.tooltip.text = 'Haz clic sobre un elemento para eliminar';
        LAny.drawLocal.edit.toolbar.buttons.editDisabled = 'No hay elementos para editar';
        LAny.drawLocal.edit.toolbar.buttons.removeDisabled = 'No hay elementos para eliminar';

        loadData();
    }, []);

    const [datosRutasTroncales, setDatosRutasTroncales] = useState<any>(null);
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await obtenerRutasTroncales.mutateAsync([]);
            setDatosRutasTroncales(response.data);
            setIsLoading(false);
        } catch (err: any) {
            toast.error(err?.message || "Error al cargar datos");
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ bgcolor: "info.light" }}>
                        <Typography variant="body1">
                            <strong>Nota:</strong> Se muestra la cobertura de QGIS, de la capa ( Rutas Troncal )
                        </Typography>
                        <Typography variant="body2">
                            Para más información dar clic sobre la <strong>ruta troncal</strong> en el mapa de color <strong style={{ color: 'blue' }}>azul</strong>.
                        </Typography>
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <div style={{ position: "relative" }}>

                        {isLoading && (
                            <div style={{
                                position: "absolute",
                                zIndex: 1000,
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                background: "rgba(255,255,255,0.6)"
                            }}>
                                <span>Cargando mapa...</span>
                            </div>
                        )}
                        <MapContainer center={[0, 0]} zoom={11} style={{ height: '90vh', width: '100%' }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <SearchControl />

                            <FeatureGroup>
                                <EditControl
                                    position="topright"
                                    onCreated={(e: any) => {
                                        const layer = e.layer;
                                        if (e.layerType === 'polyline') {
                                            const latlngs = layer.getLatLngs();

                                            let totalDistance = 0;
                                            for (let i = 0; i < latlngs.length - 1; i++) {
                                                totalDistance += latlngs[i].distanceTo(latlngs[i + 1]);
                                            }
                                            const km = (totalDistance / 1000).toFixed(2);
                                            layer.bindPopup(`Distancia: ${km} km`).openPopup();

                                            layer.setStyle({
                                                color: 'red',
                                                weight: 4,
                                            });
                                        }
                                    }}
                                    draw={{
                                        rectangle: false,
                                        polygon: false,
                                        circle: false,
                                        marker: false,
                                        circlemarker: false,
                                        polyline: true,
                                    }}
                                    edit={{
                                        featureGroup: undefined,
                                        edit: {
                                            selectedPathOptions: {
                                                maintainColor: false,
                                                color: 'red',
                                                opacity: 0.8,
                                            },
                                        },
                                        remove: true,
                                    }}
                                />
                            </FeatureGroup>

                            {datosRutasTroncales?.features?.map((feature: any) => {
                                const geometry = feature.geometry;
                                if (!geometry) return null;
                                // MultiLineString
                                if (geometry.type === "MultiLineString") {
                                    return geometry.coordinates.map((line: any, index: number) => {
                                        const positions = line.map(([lng, lat]: [number, number]) => [lat, lng]);
                                        const longitud = parseFloat(feature.properties.longitud);
                                        return (
                                            <Polyline
                                                key={`${feature.id}-${index}`}
                                                positions={positions}
                                                color="blue"
                                                weight={3}
                                                opacity={0.8}
                                            >
                                                <LeafletPopup>
                                                    <Grid container spacing={1} >
                                                        <Grid item xs={12}>
                                                            <Typography variant='subtitle1'>{feature.properties.nombre}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant='subtitle2'># Hilos: {feature.properties.hilos}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant='subtitle2'>Ciudad: {feature.properties.ciudad}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant='subtitle2'>Longitud: {longitud.toFixed(2)}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </LeafletPopup>
                                            </Polyline>
                                        );
                                    });
                                }
                                return null;
                            })}
                            <ChangeMapView center={centerMap} />
                        </MapContainer>
                    </div>
                </Grid>
            </Grid>
        </>
    );
}